import React, { useRef, useState, useEffect } from 'react';
import { ContentCategory } from '@/types/content';
import ContentCard from './ContentCard';
import { ChevronLeft, ChevronRight } from 'lucide-react';
import { cn } from '@/lib/utils';
import { useIsMobile } from '@/hooks/use-mobile';
import { useLocation } from 'react-router-dom';
import HorizontalScroller from '@/components/mobile/HorizontalScroller';

interface ContentRowProps {
  category: ContentCategory;
  className?: string;
}

const ContentRow: React.FC<ContentRowProps> = ({ category, className }) => {
  const rowRef = useRef<HTMLDivElement>(null);
  const isMobile = useIsMobile();
  const location = useLocation();
  const isVideoLibrary = location.pathname === '/videos';
  const searchParams = new URLSearchParams(location.search);
  const categoryParam = searchParams.get('category');
  const isSpecificCategory = categoryParam && categoryParam !== 'all';
  const [canScroll, setCanScroll] = useState({
    left: false,
    right: false
  });

  const updateScrollButtons = () => {
    if (rowRef.current) {
      const { scrollLeft, scrollWidth, clientWidth } = rowRef.current;
      setCanScroll({
        left: scrollLeft > 0,
        right: scrollLeft < scrollWidth - clientWidth - 5
      });
    }
  };

  useEffect(() => {
    updateScrollButtons();
    window.addEventListener('resize', updateScrollButtons);
    return () => window.removeEventListener('resize', updateScrollButtons);
  }, []);

  const scroll = (direction: 'left' | 'right') => {
    if (rowRef.current) {
      const { current } = rowRef;
      const scrollAmount = isMobile ? 250 : 400;
      const scrollLeft = direction === 'left' 
        ? current.scrollLeft - scrollAmount 
        : current.scrollLeft + scrollAmount;
      
      current.scrollTo({
        left: scrollLeft,
        behavior: 'smooth'
      });
    }
  };

  return (
    <div className={cn('mb-8', className)}>
      <div className="relative group">
        {/* Scroll Buttons - Hide when showing specific category vertically */}
        {!isMobile && !(isVideoLibrary && isSpecificCategory) && (
          <>
            <button 
              onClick={() => scroll('left')}
              className={cn(
                "absolute left-0 top-1/2 transform -translate-y-1/2 z-10 bg-black/80 rounded-full p-2 transition-opacity duration-300 -ml-4 hover:bg-black",
                canScroll.left ? "opacity-100" : "opacity-0 pointer-events-none"
              )}
              aria-label="Scroll left"
            >
              <ChevronLeft className="text-white" />
            </button>
            
            <button 
              onClick={() => scroll('right')}
              className={cn(
                "absolute right-0 top-1/2 transform -translate-y-1/2 z-10 bg-black/80 rounded-full p-2 transition-opacity duration-300 -mr-4 hover:bg-black",
                canScroll.right ? "opacity-100" : "opacity-0 pointer-events-none"
              )}
              aria-label="Scroll right"
            >
              <ChevronRight className="text-white" />
            </button>
          </>
        )}
        
        {/* Content Row */}
        {isMobile ? (
          <HorizontalScroller showNextCard={false} noLeftPadding={true} snap spacing={isVideoLibrary ? 'custom-5px' : 'md'} disableScale={isVideoLibrary}>
            {category.contents.map((content) => (
              <ContentCard 
                key={content.id} 
                content={content}
              />
            ))}
          </HorizontalScroller>
        ) : isVideoLibrary ? (
          <div 
            ref={rowRef}
            className={cn(
              "pb-4",
              isSpecificCategory 
                ? "grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6 max-w-none" 
                : "grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-4 overflow-x-auto scrollbar-hide"
            )}
            style={isSpecificCategory ? {} : { 
              scrollbarWidth: 'none', 
              msOverflowStyle: 'none'
            }}
            onScroll={isSpecificCategory ? undefined : updateScrollButtons}
          >
            {category.contents.map((content) => (
              <ContentCard 
                key={content.id} 
                content={content}
                className="w-full"
              />
            ))}
          </div>
        ) : (
          <div 
            ref={rowRef}
            className="grid grid-flow-col auto-cols-max gap-4 pb-4 overflow-x-auto scrollbar-hide"
            style={{ scrollbarWidth: 'none', msOverflowStyle: 'none' }}
            onScroll={updateScrollButtons}
          >
            {category.contents.map((content) => (
              <ContentCard 
                key={content.id} 
                content={content}
                className="transition duration-300 flex-shrink-0"
              />
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default ContentRow;
